import { NavLink } from 'react-router-dom';
import { addModifiers } from '../../shared/helpers';
import { localeData } from '../../locales/data';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorBoundary } from '../../containers';
import { CompetitionsDropdown } from './CompetitionsDropdown';
import * as React from 'react';
import { BRAIN_LABS_URL, COMMUNITY_TOPICS_URL, IQC_COMPETITION_ID, IQC_LANDING, WORLDQUANTBRAIN_SITE_URL } from '../../shared/constants';
import analytics from '../../shared/services/analytics/analytics';
import { alphasTypeSelector } from '../../reducers/AlphasReducer/AlphasSelector';
import {
  hasConsultantOrResearcherPermission,
  hasConsultantPermission,
  hasEmployeePermission,
  hasBrainLabsPermission,
  hasResearcherPermission,
  getIsLoggedInSelector,
  getUserNeedsRecertificationSelector,
} from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { competitionUserCompetitionsDropdownOptionsSelector } from '../../reducers/CompetitionsReducer/CompetitionsSelector';
import { getIsChangingTabPositionSelector } from '../../reducers/EditorReducer/EditorSelector';
import { selectionHasSimulationsUnread } from '../../reducers/GenericSelectors';
import { userProfilePerformanceLeaderboardActualWeightRankSelector } from '../../reducers/ProfileReducer/UserReducer/UserProfileSelector';
import { dataTeamIsFetchedSelector, dataTeamIsInAnActiveTeam } from '../../reducers/TeamReducer/DataTeamReducer/DataTeamSelector';
import { toggleGuidelinesModal } from '../../actions/CompetitionsActions';

interface LeftHeaderProps {
  openMobileMenu: boolean;
  navClassName?: string;
}

export function LeftHeader({ openMobileMenu, navClassName = 'header__nav' }: LeftHeaderProps) {
  const dispatch = useDispatch();

  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const isEmployee = useSelector(hasEmployeePermission);
  const isConsultant = useSelector(hasConsultantPermission);
  const isRegularUser = !useSelector(hasConsultantOrResearcherPermission);
  const isResearcher = useSelector(hasResearcherPermission);
  const recert = useSelector(getUserNeedsRecertificationSelector);
  const isLabsUsers = useSelector(hasBrainLabsPermission);
  const competitions = useSelector(competitionUserCompetitionsDropdownOptionsSelector);

  const openGuidelinesModal = () => dispatch(toggleGuidelinesModal(true));

  return (
    <nav className={navClassName}>
      {isLoggedIn && <SimulateLink navClassName={navClassName} isConsultant={isConsultant} message="header.simulate" />}
      {isLoggedIn && <AlphasLink navClassName={navClassName} isConsultant={isConsultant} />}
      {isLoggedIn && <LearnLink navClassName={navClassName} isConsultant={isConsultant} />}
      {isLoggedIn && <DataLink navClassName={navClassName} isConsultant={isConsultant} />}
      {isLoggedIn && isLabsUsers && <BrainLabsLink navClassName={navClassName} isConsultant={isConsultant} />}
      {!recert && (
        <GeniusLink navClassName={navClassName} isEmployee={isEmployee} isResearcher={isResearcher} isConsultant={isConsultant} />
      )}
      {!recert && isLoggedIn && (
        <CompetitionLink
          navClassName={navClassName}
          competitions={competitions}
          openGuidelinesModal={openGuidelinesModal}
          openMobileMenu={openMobileMenu}
        />
      )}

      {/* Previously we only display this link for regular users. Since BRAINTECH-2258, we have team-based contests, */}
      {/* in which a team can consists consultants rather than regular users. Some consultants will be assigned to a team. */}
      {/* Hence, we will show this link for regular users and consultants who are in an active team. */}
      {isLoggedIn && <TeamLink navClassName={navClassName} isConsultant={isConsultant} isResearcher={isResearcher} />}
      {!recert && isLoggedIn && <CommunityLink navClassName={navClassName} isConsultant={isConsultant} />}
      {isRegularUser && (
        <IQCLink navClassName={navClassName} isConsultant={isConsultant} isLoggedIn={isLoggedIn} competitions={competitions} />
      )}
      {!recert && !isConsultant && !isResearcher && (
        <ConsultantProgramLink isConsultant={isConsultant} navClassName={navClassName} isLoggedIn={isLoggedIn} />
      )}
      {!isLoggedIn && <EventsLink navClassName={navClassName} isConsultant={isConsultant} />}
      {!recert && <ReferralsLink navClassName={navClassName} isConsultant={isConsultant} isLoggedIn={isLoggedIn} />}
    </nav>
  );
}

interface HeaderLinkProps {
  navClassName: string;
  isConsultant: boolean;
}

interface SimulateLinkProps extends HeaderLinkProps {
  message: string;
}

function SimulateLink({ navClassName, isConsultant, message }: SimulateLinkProps) {
  const hasSimulationsUnread = useSelector(selectionHasSimulationsUnread);
  return (
    <NavLink to="/simulate" activeClassName="active" className={`${navClassName}-link simulate`}>
      <div className={addModifiers('header__img', isConsultant ? ['simulate-consultant'] : ['simulate'])}>
        <FormattedMessage id={message} defaultMessage={localeData.en[message]} />
      </div>
      {hasSimulationsUnread && <span className={`header__nav-link__unread-dot${isConsultant ? '' : ' simulate-regular'}`} />}
    </NavLink>
  );
}
const alphasSubPath: any = {
  unsubmitted: 'unsubmitted',
  submitted: 'submitted',
  alphaList: 'lists',
  alphaDistribution: 'distribution',
};

function AlphasLink({ navClassName, isConsultant }: HeaderLinkProps) {
  const alphasType = useSelector(alphasTypeSelector);
  return (
    <NavLink
      to={`/alphas/${alphasSubPath[alphasType] ? alphasSubPath[alphasType] : alphasSubPath.unsubmitted}`}
      activeClassName="active"
      className={`${navClassName}-link`}
    >
      <div className={addModifiers('header__img', isConsultant ? ['alpha-consultant'] : ['alpha'])}>
        <FormattedMessage id={'header.alphas'} defaultMessage={localeData.en['header.alphas']} />
      </div>
    </NavLink>
  );
}

function DataLink({ navClassName, isConsultant }: HeaderLinkProps) {
  return (
    <NavLink to="/data" activeClassName="active" className={`${navClassName}-link`}>
      <div className={addModifiers('intro-dashboard-step-3 header__img', isConsultant ? ['data-consultant'] : ['data'])}>
        <FormattedMessage id={'header.data'} defaultMessage={localeData.en['header.data']} />
      </div>
    </NavLink>
  );
}
interface CompetitionLinkProps {
  navClassName: string;
  competitions: any;

  openGuidelinesModal: () => void;
  openMobileMenu: boolean;
}
function CompetitionLink({
  navClassName,
  competitions,

  openGuidelinesModal,
  openMobileMenu,
}: CompetitionLinkProps) {
  const rank = useSelector(userProfilePerformanceLeaderboardActualWeightRankSelector);
  const isChangingTabPosition = useSelector(getIsChangingTabPositionSelector);
  return (
    <ErrorBoundary hideElement={true}>
      <CompetitionsDropdown
        navClassName={navClassName}
        competitions={competitions?.toJS()}
        rank={rank}
        openGuidelinesModal={openGuidelinesModal}
        openMobileMenu={openMobileMenu}
        isChangingTabPosition={isChangingTabPosition}
      />
    </ErrorBoundary>
  );
}

interface TeamLinkProps extends HeaderLinkProps {
  isResearcher: boolean;
}

function TeamLink({ navClassName, isConsultant, isResearcher }: TeamLinkProps) {
  const isInAnActiveTeam = useSelector(dataTeamIsInAnActiveTeam);
  const isFetchedTeams = useSelector(dataTeamIsFetchedSelector);
  if ((isConsultant || isResearcher) && !(isFetchedTeams && isInAnActiveTeam)) {
    return <></>;
  }

  return (
    <NavLink to="/team/active" activeClassName="active" className={`${navClassName}-link`}>
      <div className={addModifiers('intro-dashboard-step-consultant-4 header__img', isConsultant ? ['team-consultant'] : ['team'])}>
        <FormattedMessage id={'header.team'} defaultMessage={localeData.en['header.team']} />
      </div>
    </NavLink>
  );
}

function CommunityLink({ navClassName, isConsultant }: HeaderLinkProps) {
  const handleClick = () => {
    analytics.trackCustomEvent('header_click_community');
  };

  return (
    <a
      href={COMMUNITY_TOPICS_URL}
      target="blank"
      rel="noopener noreferrer"
      aria-label={'Community'}
      className={`${navClassName}-link`}
      onClick={handleClick}
    >
      <div className={addModifiers('header__img', isConsultant ? ['community-consultant'] : ['community'])}>
        <FormattedMessage id={'header.community'} defaultMessage={localeData.en['header.community']} />
      </div>
    </a>
  );
}

interface ConsultantProgramLinkProps extends HeaderLinkProps {
  isLoggedIn: boolean;
}

export function ConsultantProgramLink({ navClassName, isLoggedIn }: ConsultantProgramLinkProps) {
  const handleClick = () => {
    analytics.trackCustomEvent('header_click_consultant_program', { transport_type: 'beacon' });
  };

  if (!isLoggedIn) {
    return (
      <a
        href={`${WORLDQUANTBRAIN_SITE_URL}/consultant`}
        className={addModifiers(`${navClassName}-link`, ['consultant'])}
        rel="noreferrer"
        onClick={handleClick}
      >
        <div className={addModifiers('header__img', ['consultant'])}>
          <FormattedMessage id={'header.consultant'} defaultMessage={localeData.en['header.consultant']} />
        </div>
      </a>
    );
  }
  return (
    <NavLink
      to={'/consultant-program/'}
      activeClassName="active"
      className={addModifiers(`${navClassName}-link`, ['consultant'])}
      onClick={handleClick}
    >
      <div className={addModifiers('header__img', ['consultant'])}>
        <FormattedMessage id={'header.consultant'} defaultMessage={localeData.en['header.consultant']} />
      </div>
    </NavLink>
  );
}

interface ReferralsLinkProps extends HeaderLinkProps {
  isLoggedIn: boolean;
}

export function ReferralsLink({ navClassName, isConsultant, isLoggedIn }: ReferralsLinkProps) {
  const handleClick = () => {
    analytics.trackCustomEvent('header_click_refer_a_friend', { transport_type: 'beacon' });
  };

  if (!isLoggedIn) {
    return (
      <a
        href={`${WORLDQUANTBRAIN_SITE_URL}/referral`}
        className={addModifiers(`${navClassName}-link`, ['referrals'])}
        rel="noreferrer"
        onClick={handleClick}
      >
        <div className={addModifiers('header__img', isConsultant ? ['referrals-consultant'] : ['referrals'])}>
          <FormattedMessage id={'header.referrals'} defaultMessage={localeData.en['header.referrals']} />
        </div>
      </a>
    );
  }
  return (
    <NavLink to="/referral" activeClassName="active" className={addModifiers(`${navClassName}-link`, ['referrals'])} onClick={handleClick}>
      <div className={addModifiers('header__img', isConsultant ? ['referrals-consultant'] : ['referrals'])}>
        <FormattedMessage id={'header.referrals'} defaultMessage={localeData.en['header.referrals']} />
      </div>
    </NavLink>
  );
}

interface GeniusLinkProps extends HeaderLinkProps {
  isEmployee: boolean;
  isResearcher: boolean;
}

function GeniusLink({ navClassName, isEmployee, isResearcher, isConsultant }: GeniusLinkProps) {
  if (!isEmployee && !isResearcher && !isConsultant) {
    return null;
  }
  return (
    <NavLink to="/genius/" activeClassName="active" className={addModifiers(`${navClassName}-link`, ['genius'])}>
      <div className={addModifiers('intro-dashboard-step-consultant-4 header__img', isConsultant ? ['genius-consultant'] : ['genius'])}>
        <FormattedMessage id={'header.genius'} defaultMessage={localeData.en['header.genius']} />
      </div>
    </NavLink>
  );
}

function LearnLink({ navClassName, isConsultant }: HeaderLinkProps) {
  return (
    <NavLink to="/learn" activeClassName="active" className={`${navClassName}-link`}>
      <div className={addModifiers('intro-dashboard-step-3 header__img', isConsultant ? ['learn-consultant'] : ['learn'])}>
        <FormattedMessage id={'header.learn'} defaultMessage={localeData.en['header.learn']} />
      </div>
    </NavLink>
  );
}
function BrainLabsLink({ navClassName, isConsultant }: HeaderLinkProps) {
  return (
    <NavLink to={BRAIN_LABS_URL} className={`${navClassName}-link`}>
      <div className={addModifiers('header__img', isConsultant ? ['labs-consultant'] : ['labs'])}>
        <FormattedMessage id={'header.brainLabs'} defaultMessage={localeData.en['header.brainLabs']} />
      </div>
    </NavLink>
  );
}
export function EventsLink({ navClassName, isConsultant }: HeaderLinkProps) {
  const handleClick = () => {
    analytics.trackCustomEvent('header_click_events');
  };

  return (
    <NavLink to="/events" activeClassName="active" className={`${navClassName}-link`} onClick={handleClick}>
      <div className={addModifiers('header__img', isConsultant ? ['event-consultant'] : ['event'])}>
        <FormattedMessage id={'header.events'} defaultMessage={localeData.en['header.events']} />
      </div>
    </NavLink>
  );
}

// Uncomment the following code if you want to include IQCLink in the header
interface IqcLinkProps extends HeaderLinkProps {
  isLoggedIn: boolean;
  competitions?: any;
}

/**
 * Returns true when the current user has an IQC competition entry whose
 * status is PENDING or REJECTED. Used to display a notification dot on the
 * IQC header link prompting the user to complete enrollment.
 */
export function shouldShowIqcNotificationDot(competitions: any): boolean {
  if (!competitions || typeof competitions.find !== 'function') {
    return false;
  }
  const iqcCompetition = competitions.find(
    (competition: any) => competition && competition.get && competition.get('id') === IQC_COMPETITION_ID
  );
  if (!iqcCompetition || !iqcCompetition.get) {
    return false;
  }
  const status = iqcCompetition.get('status');
  return status === 'PENDING' || status === 'REJECTED';
}

export function IQCLink({ navClassName, isLoggedIn, competitions }: IqcLinkProps) {
  const showDot = isLoggedIn && shouldShowIqcNotificationDot(competitions);

  const handleClick = () => {
    analytics.trackCustomEvent('header_click_iqc');
  };

  if (isLoggedIn) {
    return (
      <NavLink to={IQC_LANDING} className={addModifiers(`${navClassName}-link`, ['iqc'])} onClick={handleClick}>
        <div className={addModifiers('header__img', ['iqc'])}>
          <FormattedMessage id={'header.iqc'} defaultMessage={localeData.en['header.iqc']} />
        </div>
        {showDot && <span className="header__nav-link__iqc-dot" data-testid="iqc-notification-dot" />}
      </NavLink>
    );
  }

  return (
    <a
      href={`${WORLDQUANTBRAIN_SITE_URL}/iqc`}
      className={addModifiers(`${navClassName}-link`, ['iqc'])}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
    >
      <div className={addModifiers('header__img', ['iqc'])}>
        <FormattedMessage id={'header.iqc'} defaultMessage={localeData.en['header.iqc']} />
      </div>
    </a>
  );
}
